import { AxiosResponse } from 'axios';
import { BaseApiService } from './BaseApiService';
import { API_ENDPOINTS } from '@/constants/apiConfig';
import { Email, EmailListResponse } from '@/types';

/**
 * Response structure for email actions (accept/reject)
 * @interface EmailActionResponse
 * @property {number} total_records - Total number of records affected
 * @property {boolean} success - Indicates if the action was successful
 * @property {string} [message] - Optional message providing additional information
 */
export interface EmailActionResponse {
  total_records: number;
  success: boolean;
  message?: string;
}

/**
 * Email API Service
 * 
 * Provides methods to interact with email-related API endpoints including:
 * - Fetching email lists with pagination and search
 * - Retrieving detailed email information
 * - Performing actions on emails (accept/reject)
 * - Parsing and normalizing email data from various response formats
 * 
 * @class EmailApiService
 * @extends BaseApiService
 * @example
 * const emailService = new EmailApiService();
 * 
 * // Fetch paginated emails
 * const response = await emailService.getEmails(false, { page: 1, rowsPerPage: 10 });
 * 
 * // Get email details
 * const email = await emailService.getEmailById('123');
 * 
 * // Perform actions on email
 * await emailService.submitEmailAction('123', 'accepted', 'Approved by admin');
 */
export class EmailApiService extends BaseApiService {
  /**
   * Get list of emails
   */
  /**
   * Fetches a paginated list of emails with optional search and caching
   * 
   * @param {boolean} [useDevCors=false] - Whether to use CORS for development
   * @param {Object} [options] - Configuration options
   * @param {boolean} [options.cache=true] - Enable response caching
   * @param {boolean} [options.forceRefresh=false] - Bypass cache and force refresh
   * @param {number} [options.page=1] - Page number (1-based)
   * @param {number} [options.rowsPerPage=10] - Number of items per page
   * @param {string} [options.searchQuery=''] - Optional search query string
   * @returns {Promise<AxiosResponse<EmailListResponse>>} Promise resolving to the email list response
   * 
   * @example
   * // Fetch first page with 20 items per page
   * const response = await emailService.getEmails(false, {
   *   page: 1,
   *   rowsPerPage: 20,
   *   searchQuery: 'important'
   * });
   * 
   * @description
   * This method handles:
   * - Pagination (1-based index)
   * - Search functionality
   * - Response caching
   * - Query parameter construction
   */
  async getEmails(
    useDevCors: boolean = false,
    options: { 
      cache?: boolean; 
      forceRefresh?: boolean;
      page?: number;
      rowsPerPage?: number;
      searchQuery?: string;
      category?: string;
      startDate?: string;
      endDate?: string;
    } = { 
      cache: true, 
      forceRefresh: false,
      page: 1,
      rowsPerPage: 10,
      searchQuery: ''
    }
  ): Promise<AxiosResponse<EmailListResponse>> {
    // Convert to 1-based page for the API
    const current_page = options.page || 1;
    const row_per_page = options.rowsPerPage || 10;
    
    // Build query parameters object
    const queryParams: Record<string, string> = {
      current_page: current_page.toString(),
      row_per_page: row_per_page.toString()
    };
    
    // Add search query if provided
    if (options.searchQuery && options.searchQuery.trim() !== '') {
      queryParams.search_query = options.searchQuery.trim();
    }
    
    // Add category filter if provided
    if (options.category) {
      queryParams.category = options.category;
    }
    
    // Add date range filters if provided
    if (options.startDate) {
      queryParams.start_date = options.startDate;
    }
    
    if (options.endDate) {
      queryParams.end_date = options.endDate;
    }
    
    // Log the URL that will be requested
    const url = API_ENDPOINTS.EMAILS.LIST;
    console.log('API Request URL:', `${url}?${new URLSearchParams(queryParams).toString()}`);
    
    // Pass the query parameters as the params option to this.get()
    return this.get<EmailListResponse>(url, queryParams);
  }


  /**
   * Get email details by ID
   */
  /**
   * Retrieves detailed information for a specific email by its ID
   * 
   * @param {string|number} emailId - The unique identifier of the email
   * @param {boolean} [useDevCors=false] - Whether to use CORS for development
   * @param {Object} [options] - Configuration options
   * @param {boolean} [options.cache=true] - Enable response caching
   * @param {boolean} [options.forceRefresh=false] - Bypass cache and force refresh
   * @returns {Promise<AxiosResponse<Email>>} Promise resolving to the email details
   * 
   * @example
   * // Get email with ID '123' and bypass cache
   * const response = await emailService.getEmailById('123', false, { forceRefresh: true });
   * const email = response.data;
   * 
   * @description
   * This method:
   * - Fetches a single email by its unique identifier
   * - Supports caching to reduce network requests
   * - Handles both string and numeric email IDs
   * - Returns detailed email information including headers, body, and attachments
   */
 async getEmailById(
  emailId: string | number,
  useDevCors: boolean = false,
  options: { cache?: boolean; forceRefresh?: boolean } = {
    cache: true,
    forceRefresh: false
  }
): Promise<AxiosResponse<Email>> {
  return this.get<Email>(
    API_ENDPOINTS.EMAILS.DETAILS(emailId),
    {},
    useDevCors,
    { noCache: !options.cache || options.forceRefresh }
  );
}
  
async getQuestionById(
  id: string | number,
  useDevCors: boolean = false
): Promise<any> {
  return this.get(
    API_ENDPOINTS.EMAILS.EDIT_QUESTIONS(id),
    {},
    useDevCors
  );
}

  /**
   * Submit an email action (accept/reject)
   * @param emailId The ID of the email
   * @param action The action to perform ('accepted' or 'rejected')
   * @param notes Optional notes for the action
   * @param useDevCors Whether to use CORS for development
   */
  /**
   * Submits an action (accept/reject) for a specific email
   * 
   * @param {string|number} emailId - The ID of the email to perform the action on
   * @param {'accepted'|'rejected'} action - The action to perform
   * @param {string} [notes=''] - Optional notes or comments about the action
   * @param {boolean} [useDevCors=false] - Whether to use CORS for development
   * @returns {Promise<AxiosResponse<EmailActionResponse>>} Promise resolving to the action response
   * 
   * @example
   * // Accept an email with a note
   * await emailService.submitEmailAction(
   *   '123',
   *   'accepted',
   *   'Approved by admin',
   *   false
   * );
   * 
   * @description
   * This method:
   * - Submits an action (accept/reject) for the specified email
   * - Includes optional notes with the action
   * - Automatically adds a timestamp to the request
   * - Returns the server's response to the action
   */
  async submitEmailAction(
    emailId: string | number,
    action: 'accepted' | 'rejected',
    userName: string | null,
    notes: string = '',
    useDevCors: boolean = false
  ): Promise<AxiosResponse<EmailActionResponse>> {
    // Prepare request data
    const requestData = {
      action,
      notes,
      timestamp: new Date().toISOString(),
      user_name: userName
    };
    
    // Make the request with appropriate CORS settings
    return this.post<EmailActionResponse>(
      API_ENDPOINTS.EMAILS.ACTIONS(emailId),
      requestData,
      useDevCors,
      { noRetry: false }
    );
  }

  /**
   * Normalizes email data from various API response structures into a consistent format
   * 
   * @static
   * @param {AxiosResponse} response - The raw API response
   * @returns {Email[]} Array of normalized email objects
   * 
   * @example
   * const emails = EmailApiService.extractEmailData(apiResponse);
   * 
   * @description
   * This static helper method handles different response formats from the API:
   * - Direct array of emails
   * - Nested data property with array of emails
   * - Nested emails property
   * - Nested result property
   * 
   * Returns an empty array if the response structure is not recognized.
   */
  static extractEmailData(response: AxiosResponse): Email[] {
    const { data } = response;

    // Handle different possible response structures
    if (Array.isArray(data)) {
      return data;
    } else if (Array.isArray(data?.data)) {
      return data.data;
    } else if (Array.isArray(data?.emails)) {
      return data.emails;
    } else if (Array.isArray(data?.result)) {
      return data.result;
    } else {
      // If response contains date field, it might be a single object
      console.log('Unexpected response structure:', data);
      return [];
    }
  }

  /**
   * Extracts and normalizes a single email from various API response structures
   * 
   * @static
   * @param {AxiosResponse} response - The raw API response
   * @returns {Email | null} The normalized email object or null if not found
   * 
   * @example
   * const email = EmailApiService.extractSingleEmailData(apiResponse);
   * if (email) {
   *   console.log('Email subject:', email.subject);
   * }
   * 
   * @description
   * This static helper method handles different response formats:
   * - Direct email object
   * - Nested data property containing the email
   * - First item of an array response
   * 
   * Returns null if no valid email data is found in the response.
   */
  static extractSingleEmailData(response: AxiosResponse): Email | null {
    const { data } = response;

    // Handle the new response structure: { data: { ...emailData } }
    if (typeof data?.data === 'object') {
      return data.data as Email;
    }

    // Handle different possible response structures (legacy support)
    if (Array.isArray(data)) {
      return data[0] || null; // Take first item if array
    } else if (data && typeof data === 'object') {
      return data as Email;
    } else {
      return null;
    }
  }
}
